"use client";

import * as React from "react";
import { Slider } from "@/app/components/ui/slider";
import { Label } from "@/app/components/ui/label";
import { cn } from "@/app/lib/utils";

export interface QuantitySliderProps {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onValueChange: (value: number) => void;
  hint?: string;
  valueSuffix?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Reusable quantity slider with label and optional hint.
 * Uses a single-thumb slider; value is clamped to [min, max].
 */
export function QuantitySlider({
  label,
  value,
  min = 1,
  max = 100,
  step = 1,
  onValueChange,
  hint,
  valueSuffix = "",
  className,
  disabled = false,
}: QuantitySliderProps) {
  const clamped = Math.min(max, Math.max(min, value));

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        <span
          className="tabular-nums text-sm font-medium text-foreground"
          aria-hidden
        >
          {clamped}
          {valueSuffix}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[clamped]}
        onValueChange={(v) => onValueChange(v[0] ?? clamped)}
        disabled={disabled}
        className="w-full"
      />
      {hint && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
